import { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import "./index.css";
import lemdaLogo from "./lemda.png";

type Section =
  | "overview" | "architecture" | "phases"
  | "agent-vs-chatbot" | "harness" | "tools" | "context" | "memory" | "safety" | "evaluation"
  | "getting-started"
  | "custom-models" | "custom-providers"
  | "session-format" | "programmatic-usage" | "sdk" | "rpc-mode" | "json-event-stream" | "tui-components"
  | "windows" | "termux" | "tmux" | "terminal-setup" | "shell-aliases"
  | "development";

type TopTab = "home" | "documentation" | "news" | "packages" | "models";

interface NavItem { id: Section; label: string }
interface NavSection { title: string; items: NavItem[] }

const TOP_TABS: { id: TopTab; label: string }[] = [
  { id: "home", label: "Home" },
  { id: "documentation", label: "Documentation" },
  { id: "news", label: "News" },
  { id: "packages", label: "Packages" },
  { id: "models", label: "Models" },
];

const SECTIONS: NavSection[] = [
  { title: "Start here", items: [{ id: "overview", label: "Overview" }, { id: "getting-started", label: "Quickstart" }] },
  { title: "Core Concepts", items: [{ id: "agent-vs-chatbot", label: "Agent vs Chatbot" }, { id: "harness", label: "The Harness" }, { id: "tools", label: "Tools" }, { id: "context", label: "Context" }, { id: "memory", label: "Memory" }, { id: "safety", label: "Safety & Permissions" }, { id: "evaluation", label: "Evaluation" }] },
  { title: "Architecture", items: [{ id: "architecture", label: "Architecture" }, { id: "phases", label: "Development Phases" }] },
  { title: "Customization", items: [{ id: "custom-models", label: "Custom Models" }, { id: "custom-providers", label: "Custom Providers" }] },
  { title: "Reference", items: [{ id: "session-format", label: "Session Format" }, { id: "programmatic-usage", label: "Programmatic Usage" }, { id: "sdk", label: "SDK" }, { id: "rpc-mode", label: "RPC Mode" }, { id: "json-event-stream", label: "JSON Event Stream Mode" }, { id: "tui-components", label: "TUI Components" }] },
  { title: "Platform Setup", items: [{ id: "windows", label: "Windows" }, { id: "termux", label: "Termux on Android" }, { id: "tmux", label: "tmux" }, { id: "terminal-setup", label: "Terminal Setup" }, { id: "shell-aliases", label: "Shell Aliases" }] },
  { title: "Development", items: [{ id: "development", label: "Development" }] },
];

function SearchModal({ open, query, setQuery, onSelect, onClose }: {
  open: boolean; query: string; setQuery: (q: string) => void;
  onSelect: (s: Section) => void; onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const allItems = SECTIONS.flatMap((s) => s.items.map((i) => ({ ...i, section: s.title })));
  const results = query.trim()
    ? allItems.filter((i) => i.label.toLowerCase().includes(query.toLowerCase()) || i.id.includes(query.toLowerCase()))
    : [];

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" onClick={onClose}>
      <div className="fixed inset-0 bg-black/60" />
      <div className="relative w-full max-w-lg bg-popover border border-border rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <svg className="size-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documentation..."
            className="flex-1 bg-transparent border-none outline-none text-sm text-foreground placeholder:text-muted-foreground/50"
          />
          <kbd className="text-[10px] text-muted-foreground/40 border border-border rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        {query.trim() && (
          <div className="max-h-80 overflow-y-auto p-2">
            {results.length === 0 ? (
              <p className="text-sm text-muted-foreground/50 text-center py-8">No results for &ldquo;{query}&rdquo;</p>
            ) : (
              <div className="space-y-0.5">
                {results.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => { onSelect(item.id); onClose(); setQuery(""); }}
                    className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-accent transition-colors"
                  >
                    <span className="text-foreground">{item.label}</span>
                    <span className="text-xs text-muted-foreground/50 ml-2">&middot; {item.section}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Sidebar({ active, onSelect, onSearchOpen }: { active: Section; onSelect: (s: Section) => void; onSearchOpen: () => void }) {
  return (
    <aside className="w-64 shrink-0 border-r border-border h-[calc(100vh-3rem)] sticky top-12 overflow-y-auto bg-sidebar text-sidebar-foreground flex flex-col">
      <div className="px-4 pt-5 pb-3 shrink-0 space-y-3">
        <p className="text-[11px] text-sidebar-foreground/50 leading-relaxed">
          Guides and references for building and extending Lemda.
        </p>
        <button onClick={onSearchOpen} className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md border border-sidebar-border bg-sidebar-accent/20 text-xs text-sidebar-foreground/40 hover:text-sidebar-foreground/60 transition-colors text-left">
          <svg className="size-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="flex-1">Search documentation...</span>
          <kbd className="hidden md:inline-flex items-center gap-0.5 rounded border border-sidebar-border bg-sidebar px-1.5 py-0.5 text-[10px] font-medium text-sidebar-foreground/40">
            <span className="text-[9px]">&#8984;</span>K
          </kbd>
        </button>
      </div>

      <div className="px-3 pb-1 shrink-0">
        <p className="text-[11px] font-semibold tracking-wider text-sidebar-foreground/50 px-3 uppercase">Documentation</p>
      </div>

      <nav className="flex-1 px-3 pb-4 space-y-5 overflow-y-auto">
        {SECTIONS.map((section) => (
          <div key={section.title}>
            <p className="text-[11px] font-semibold tracking-wider text-sidebar-foreground/40 px-3 mb-0.5">{section.title}</p>
            {section.items.map((item) => (
              <button
                key={item.id}
                onClick={() => onSelect(item.id)}
                className={cn(
                  "w-full text-left px-3 py-1 rounded-md text-sm transition-colors",
                  active === item.id
                    ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-sidebar-border shrink-0">
        <div className="flex items-center gap-2 px-3 py-1.5">
          <span className="size-1.5 rounded-full bg-green-500 shrink-0" />
          <span className="text-[11px] text-sidebar-foreground/40">Latest &middot; v0.1.0</span>
        </div>
      </div>
    </aside>
  );
}

function Content({ section }: { section: Section }) {
  switch (section) {
    case "overview": return <Overview />;
    case "architecture": return <Architecture />;
    case "phases": return <Phases />;
    case "agent-vs-chatbot": return <AgentVsChatbot />;
    case "harness": return <Harness />;
    case "tools": return <Tools />;
    case "context": return <Context />;
    case "memory": return <Memory />;
    case "safety": return <Safety />;
    case "evaluation": return <Evaluation />;
    case "getting-started": return <GettingStarted />;
    default: return <ComingSoon section={section} />;
  }
}

function ComingSoon({ section }: { section: string }) {
  const label = SECTIONS.flatMap((s) => s.items).find((i) => i.id === section)?.label ?? section;
  return (
    <Page>
      <SectionTitle>{label}</SectionTitle>
      <SubTitle>This page is coming soon.</SubTitle>
      <P>Lemda is being built incrementally. This section will be documented as the corresponding feature is implemented.</P>
      <div className="rounded-lg border border-border bg-card p-6 mt-6">
        <p className="text-sm text-muted-foreground">
          <strong className="text-foreground">Planned for a future phase.</strong>{" "}
          Track progress in <Code>parts/README.MD</Code>.
        </p>
      </div>
    </Page>
  );
}

function Page({ children }: { children: React.ReactNode }) {
  return <main className="flex-1 overflow-y-auto p-8 md:p-12 max-w-4xl mx-auto">{children}</main>;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-3xl font-bold tracking-tight mb-2">{children}</h2>;
}
function SubTitle({ children }: { children: React.ReactNode }) {
  return <p className="text-muted-foreground text-lg mb-8">{children}</p>;
}
function H3({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xl font-semibold mt-10 mb-3 flex items-center gap-2"><span className="size-1.5 rounded-full bg-primary shrink-0" />{children}</h3>;
}
function H4({ children }: { children: React.ReactNode }) {
  return <h4 className="text-base font-semibold mt-6 mb-2">{children}</h4>;
}
function P({ children }: { children: React.ReactNode }) {
  return <p className="text-muted-foreground mb-4 leading-relaxed">{children}</p>;
}
function Code({ children }: { children: React.ReactNode }) {
  return <code className="rounded bg-muted px-[0.3rem] py-[0.15rem] font-mono text-sm">{children}</code>;
}
function Pre({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative group mb-6">
      <div className="absolute top-0 left-0 right-0 h-9 bg-muted/80 border-b border-border rounded-t-lg flex items-center px-4">
        <div className="flex gap-1.5">
          <span className="size-2.5 rounded-full bg-red-500/80" />
          <span className="size-2.5 rounded-full bg-yellow-500/80" />
          <span className="size-2.5 rounded-full bg-green-500/80" />
        </div>
      </div>
      <pre className="bg-muted/40 pt-10 p-4 rounded-lg text-sm overflow-x-auto border border-border">{children}</pre>
    </div>
  );
}
function UL({ children }: { children: React.ReactNode }) {
  return <ul className="space-y-1.5 text-muted-foreground mb-4 [&>li]:pl-5 [&>li]:relative [&>li]:before:content-[''] [&>li]:before:absolute [&>li]:before:left-1 [&>li]:before:top-[7px] [&>li]:before:size-1 [&>li]:before:rounded-full [&>li]:before:bg-muted-foreground/40">{children}</ul>;
}

function Table({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto mb-6 rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/50">
            {headers.map((h) => (
              <th key={h} className="text-left py-2.5 px-4 font-semibold text-foreground/80">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="text-muted-foreground">
          {rows.map((row, i) => (
            <tr key={i} className={cn("border-t border-border/50", i % 2 === 0 && "bg-background/50")}>
              {row.map((cell, j) => (
                <td key={j} className="py-2 px-4">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const AGENT_LOOP_DIAGRAM = `┌────────────────────────────────────────────┐
│               Agent Loop                   │
│  ┌──────────┐     ┌──────────────────┐     │
│  │   LLM    │ ◄──► │  Tool Registry   │     │
│  └──────────┘     └──────────────────┘     │
│       ↕                     ↕              │
│  ┌──────────────┐  ┌──────────────────┐    │
│  │    Memory    │  │   Permissions    │    │
│  │   Manager    │  │   & Safety       │    │
│  └──────────────┘  └──────────────────┘    │
│       ↕                                     │
│  ┌──────────────┐                           │
│  │   Session    │                           │
│  │    Store     │                           │
│  └──────────────┘                           │
└────────────────────────────────────────────┘`;

function Overview() {
  return (
    <Page>
      <SectionTitle>What is Lemda?</SectionTitle>
      <SubTitle>A cloud agent like Devin — an AI coding agent and software engineer that helps developers build better software faster. Parallel cloud agents for serious engineering teams.</SubTitle>

      <P>
        Lemda is a cloud agent like Devin — an AI coding agent and software engineer that helps
        developers build better software faster. Parallel cloud agents for serious engineering teams.
      </P>

      <div className="bg-card border border-border rounded-lg p-6 mb-8">
        <p className="text-sm text-muted-foreground italic">
          "Give Claude Sonnet or GPT-4o a chat interface and you get a conversational AI.
          Wrap it in a loop that can call external tools, maintain state across turns,
          enforce budget limits, and validate its own outputs — and you get an agent.
          The difference isn't the LLM. It's everything around the LLM."
        </p>
        <p className="text-xs text-muted-foreground/50 mt-2">— Building an AI Agent Harness from Scratch, DEV Community 2026</p>
      </div>

      <H3>How a coding agent works</H3>
      <Pre>{`User task
  → Agent receives task
  → LLM decides next action
  → Tool executes (readFile, runCommand, ...)
  → LLM sees tool result
  → Repeat until task is complete
  → Final answer`}</Pre>

      <P>
        This repeating pattern is the <strong>agent loop</strong>. The LLM provides reasoning;
        the harness provides hands, eyes, memory, and safety boundaries. Without the harness,
        you have a stateless chat API. With it, you have a system that can independently
        navigate and modify a codebase.
      </P>

      <H3>Current status</H3>
      <P>
        Phase 1 is complete. The CLI, types, and harness stub work. There is no model
        connection yet — the agent prints a placeholder message and completes. Each phase adds
        a real capability toward a fully autonomous coding agent.
      </P>
    </Page>
  );
}

function Architecture() {
  return (
    <Page>
      <SectionTitle>Architecture</SectionTitle>
      <SubTitle>The five layers of a coding agent</SubTitle>

      <P>
        Every production coding agent — Claude Code, Codex CLI, Gemini CLI, Aider, Pi —
        shares the same fundamental architecture. The model is one component in a larger system.
      </P>

      <Pre>{AGENT_LOOP_DIAGRAM}</Pre>

      <div className="grid gap-4 sm:grid-cols-2 mb-8">
        {[
          { num: "01", title: "Agent Loop", desc: "The core control flow. Receives input, calls the model, routes tool calls, feeds results back, repeats until termination." },
          { num: "02", title: "Tool System", desc: "A registry of callable functions with name, description, and typed parameters. Validates arguments and executes safely." },
          { num: "03", title: "Memory & Context", desc: "Manages conversation history, injects relevant context, compresses old messages to stay within token limits." },
          { num: "04", title: "Safety & Permissions", desc: "Gates every tool call. Reads auto-allowed, writes require approval, destructive ops denied by default." },
          { num: "05", title: "Session Store", desc: "Persists transcripts and agent state to JSONL files, enabling recovery, auditing, and evaluation." },
        ].map((l) => (
          <div key={l.num} className="bg-card border border-border rounded-lg p-4">
            <span className="text-xs font-mono text-muted-foreground/50">{l.num}</span>
            <h4 className="font-semibold text-sm mt-1">{l.title}</h4>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{l.desc}</p>
          </div>
        ))}
      </div>
    </Page>
  );
}

function Phases() {
  return (
    <Page>
      <SectionTitle>Development Phases</SectionTitle>
      <SubTitle>Eight phases from CLI to cloud architecture</SubTitle>

      <div className="space-y-3 mb-8">
        {[
          { phase: "1.x", title: "Foundation", desc: "TypeScript CLI, project structure, config, core types.", done: true },
          { phase: "2.x", title: "Core Harness", desc: "Fake model, real tools, agent loop, permission system.", done: false },
          { phase: "3.x", title: "Real Model", desc: "Provider API, streaming responses, tool calling with a real LLM.", done: false },
          { phase: "4.x", title: "Coding Tools", desc: "File editing with diffs, command execution, interactive permissions.", done: false },
          { phase: "5.x", title: "Memory & Context", desc: "Session persistence, conversation summaries, retrieval-augmented context.", done: false },
          { phase: "6.x", title: "Evaluation", desc: "Repeatable task suite, automated graders, regression tracking.", done: false },
          { phase: "7.x", title: "Reliability", desc: "Checkpoints, token budgets, iteration limits, cancellation.", done: false },
          { phase: "8.x", title: "Cloud Architecture", desc: "Server, queue, workers, sandbox fleet.", done: false },
        ].map((p) => (
          <div key={p.phase} className={cn("flex items-start gap-4 p-4 rounded-lg border transition-colors", p.done ? "bg-card border-primary/20" : "bg-card border-border")}>
            <span className={cn("shrink-0 w-10 h-7 rounded-md flex items-center justify-center text-xs font-bold", p.done ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>{p.phase}</span>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-sm">{p.title}</p>
                {p.done && <span className="text-xs text-green-500 font-medium">Complete</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{p.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <H4>First milestone</H4>
        <P className="mb-0">
          By the end of Phase 2, typing <Code>bun run dev "Read package.json and tell me the test command"</Code> will
          make the agent call <Code>readFile</Code> on <Code>package.json</Code>, see the result, and answer
          based on what it read — all autonomously.
        </P>
      </div>
    </Page>
  );
}

function AgentVsChatbot() {
  return (
    <Page>
      <SectionTitle>Agent vs Chatbot</SectionTitle>
      <SubTitle>The fundamental difference</SubTitle>

      <div className="grid gap-6 sm:grid-cols-2 mb-8">
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="size-2 rounded-full bg-blue-500" />
            <h4 className="font-semibold text-sm">Chatbot</h4>
          </div>
          <Pre>{`User message
  → LLM
  → Answer`}</Pre>
          <P className="text-xs mb-0">One stateless exchange. No tools, no actions, no persistence beyond the current window.</P>
        </div>
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="size-2 rounded-full bg-green-500" />
            <h4 className="font-semibold text-sm">Coding Agent</h4>
          </div>
          <Pre>{`User task
  → LLM decides
  → Tool runs
  → LLM sees result
  → Repeats
  → Final answer`}</Pre>
          <P className="text-xs mb-0">Multi-turn stateful loop. Can read files, run commands, edit code — and iterate until done.</P>
        </div>
      </div>

      <H3>Why it matters</H3>
      <P>
        A chatbot answers questions. A coding agent does work. It reads your repository, understands the
        structure, makes targeted edits, runs tests, and iterates until the tests pass. This autonomy
        is what makes coding agents useful for real software engineering tasks.
      </P>
    </Page>
  );
}

function Harness() {
  return (
    <Page>
      <SectionTitle>The Harness</SectionTitle>
      <SubTitle>TypeScript code around the model that makes it an agent</SubTitle>

      <P>
        The harness is the orchestration layer between the LLM and the outside world. It is normal
        application code — in Lemda's case, TypeScript — that controls the entire agent lifecycle.
      </P>

      <div className="bg-card border border-border rounded-lg p-5 mb-6">
        <p className="text-sm font-medium text-foreground mb-1">Harness = Tools + Knowledge + Observation + Action + Permissions</p>
        <p className="text-xs text-muted-foreground">The model provides intelligence; the harness provides everything else.</p>
      </div>

      <H3>What the harness does</H3>
      <UL>
        <li>Receives your task from the terminal</li>
        <li>Builds useful context (system prompt, project instructions, relevant files)</li>
        <li>Sends context + message history to the LLM</li>
        <li>Gives the model a list of available tools with schemas</li>
        <li>Validates and executes approved tool calls</li>
        <li>Sends tool results back to the model</li>
        <li>Stops when the task is complete (or a limit is reached)</li>
        <li>Saves the session and trace for debugging and evaluation</li>
      </UL>

      <P>
        Every major coding agent — Codex CLI, Claude Code, Pi, OpenCode — has a version of this harness.
        The model is the reasoning engine; the harness provides the hands, eyes, memory, and safety boundaries.
      </P>
    </Page>
  );
}

function Tools() {
  return (
    <Page>
      <SectionTitle>Tools</SectionTitle>
      <SubTitle>How the agent interacts with your system</SubTitle>

      <P>
        The LLM cannot directly read files, edit code, or run commands. It only produces text.
        Tools are the mechanism by which the model interacts with the outside world.
      </P>

      <P>
        Each tool is a function with a name, description, and typed parameter schema. The harness
        presents these to the model, and when the model requests a tool call, the harness validates
        the arguments, executes the function, and returns the result.
      </P>

      <H3>Planned tool set</H3>
      <Table
        headers={["Tool", "Auto-allow?", "Description"]}
        rows={[
          ["listFiles(path)", <span className="text-green-500 font-medium">Yes</span>, "List files in a directory"],
          ["readFile(path)", <span className="text-green-500 font-medium">Yes</span>, "Read a file's contents"],
          ["searchText(pattern)", <span className="text-green-500 font-medium">Yes</span>, "Regex search across files"],
          ["writeFile(path, content)", <span className="text-yellow-500 font-medium">Prompt</span>, "Write or overwrite a file"],
          ["editFile(path, old, new)", <span className="text-yellow-500 font-medium">Prompt</span>, "Make a surgical text replacement"],
          ["runCommand(command)", <span className="text-yellow-500 font-medium">Prompt</span>, "Execute a shell command"],
          ["glob(pattern)", <span className="text-green-500 font-medium">Yes</span>, "Find files by name pattern"],
          ["deleteFile(path)", <span className="text-red-500 font-medium">Deny</span>, "Permanently delete a file"],
        ]}
      />

      <H3>How it works</H3>
      <Pre>{`1. Model responds with: tool_use(name="readFile", args={path: "package.json"})
2. Harness validates args against schema
3. Permission check: is readFile auto-allowed?
4. Function executes: returns file contents
5. Result sent back to model as tool_result message
6. Model continues reasoning`}</Pre>
    </Page>
  );
}

function Context() {
  return (
    <Page>
      <SectionTitle>Context</SectionTitle>
      <SubTitle>Everything the model needs to know about your project</SubTitle>

      <P>
        The model has no permanent awareness of your project unless we send information to it.
        Every run starts fresh — context must be actively built and managed.
      </P>

      <H3>What goes into context</H3>
      <div className="grid gap-3 sm:grid-cols-2 mb-6">
        {[
          ["System rules", "How the agent should behave, formatting constraints"],
          ["Current task", "The user's request from the terminal"],
          ["Project instructions", "From AGENTS.md, CLAUDE.md, or similar files"],
          ["Relevant files", "Key files in the project structure"],
          ["Recent tool outputs", "What the agent has seen and done"],
          ["Previous session summary", "Compacted history from prior runs"],
        ].map(([title, desc]) => (
          <div key={title} className="bg-card border border-border rounded-lg p-3">
            <p className="text-sm font-medium">{title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
          </div>
        ))}
      </div>

      <H3>The context engineering problem</H3>
      <P>
        Too little context causes mistakes — the model doesn't know your conventions, architecture,
        or test setup. Too much context wastes tokens (money) and confuses the model with noise.
        Managing this trade-off is <strong>context engineering</strong>, one of the most important
        skills in building effective agents.
      </P>
    </Page>
  );
}

function Memory() {
  return (
    <Page>
      <SectionTitle>Memory</SectionTitle>
      <SubTitle>From short-term messages to persistent project knowledge</SubTitle>

      <P>Memory in an AI agent operates at multiple timescales, each serving a different purpose:</P>

      <div className="space-y-3 mb-6">
        {[
          ["Short-term memory", "Messages and tool results in the current run, stored in the AgentState message array"],
          ["Session memory", "Saved transcript (JSONL) so the user can resume a previous session later"],
          ["Project memory", "Persisted facts about the project: test commands, architecture notes, conventions"],
          ["Retrieval", "Finding relevant files or past decisions when needed for the current task"],
        ].map(([title, desc]) => (
          <div key={title} className="flex gap-3 bg-card border border-border rounded-lg p-3">
            <span className="size-2 rounded-full bg-primary shrink-0 mt-1.5" />
            <div>
              <p className="text-sm font-medium">{title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      <H3>Implementation plan</H3>
      <P>
        Lemda starts simple: local JSONL session files, not a vector database. Each run appends
        to a session log. Later phases add summarization (to compress old turns) and retrieval
        (to find relevant context from past sessions).
      </P>

      <Pre>{`// Session file format (JSONL)
{"role":"user","content":"Read package.json"}
{"role":"assistant","content":"","tool_calls":[{"name":"readFile","args":{"path":"package.json"}}]}
{"role":"tool","name":"readFile","content":"{\n  \"name\": \"lemda-agent\"...}"}
{"role":"assistant","content":"The package name is lemda-agent."}`}</Pre>
    </Page>
  );
}

function Safety() {
  return (
    <Page>
      <SectionTitle>Safety & Permissions</SectionTitle>
      <SubTitle>Keeping the agent from causing harm</SubTitle>

      <P>
        LLMs make mistakes. A coding agent has direct access to your filesystem and shell,
        so safety is not optional — it is foundational.
      </P>

      <H3>Permission levels</H3>
      <Table
        headers={["Action", "Policy"]}
        rows={[
          ["Read files", <span className="text-green-500 font-medium">Allow automatically</span>],
          ["List directories", <span className="text-green-500 font-medium">Allow automatically</span>],
          ["Search text", <span className="text-green-500 font-medium">Allow automatically</span>],
          ["Write files", <span className="text-yellow-500 font-medium">Show diff, ask user</span>],
          ["Edit files", <span className="text-yellow-500 font-medium">Show diff, ask user</span>],
          ["Run test command", <span className="text-yellow-500 font-medium">Ask user initially</span>],
          ["Delete files", <span className="text-red-500 font-medium">Deny by default</span>],
          ["Network requests", <span className="text-red-500 font-medium">Deny by default</span>],
          ["Git push", <span className="text-red-500 font-medium">Deny by default</span>],
          ["Install packages", <span className="text-yellow-500 font-medium">Prompt user</span>],
        ]}
      />

      <P>
        This is the beginning of a sandbox and policy system. In later phases, the permission
        model becomes more granular: per-project policies, per-session trust levels, and a full
        audit log of every action taken.
      </P>
    </Page>
  );
}

function Evaluation() {
  return (
    <Page>
      <SectionTitle>Evaluation</SectionTitle>
      <SubTitle>Measuring agent quality systematically</SubTitle>

      <P>
        An agent is not "good" because one demo worked. Repeatable, automated evaluation is
        essential for making progress — especially when changing prompts, models, tools, or
        memory strategies.
      </P>

      <H3>What we measure</H3>
      <Table
        headers={["Dimension", "Question"]}
        rows={[
          ["Correctness", "Does the agent produce the right output?"],
          ["Safety", "Were any forbidden files or commands touched?"],
          ["Efficiency", "How many tool calls were needed?"],
          ["Cost", "How many tokens were consumed?"],
          ["Speed", "How long did the task take?"],
        ]}
      />

      <H3>Example eval task</H3>
      <Pre>{`Task: Add a health endpoint to the Express server
Success: The test suite passes
Safety: No forbidden file was changed
Efficiency: Fewer than 10 tool calls
Cost: Under 50K tokens total`}</Pre>

      <P>
        Each task becomes a test case. The harness runs the agent, then an automated grader
        checks the results — using both rule-based assertions and an LLM-as-a-judge for
        qualitative assessment. This lets us compare agent versions objectively.
      </P>
    </Page>
  );
}

function GettingStarted() {
  return (
    <Page>
      <SectionTitle>Getting Started</SectionTitle>
      <SubTitle>Run the agent in under a minute</SubTitle>

      <H3>Prerequisites</H3>
      <P>You need <strong>Bun</strong> installed:</P>
      <Pre>{`# macOS / Linux
curl -fsSL https://bun.sh/install | bash

# Verify
bun --version`}</Pre>

      <H3>Install &amp; run</H3>
      <Pre>{`cd lemda-agent
bun install
bun run dev -- "Read package.json and tell me the test command"`}</Pre>

      <div className="bg-card border border-border rounded-lg p-5 mb-6">
        <p className="text-sm font-medium mb-2">Expected output:</p>
        <Pre>{`Lemda is working on: Read package.json and tell me the test command
Lemda answer: I can receive your task, but I am not connected to an AI model yet.
Messages remembered: 2
Lemda status: completed`}</Pre>
      </div>

      <H3>Project structure</H3>
      <Pre>{`lemda/
  lemda-agent/        # The CLI agent
    index.ts          # Entry point
    package.json      # Deps & scripts
    src/
      cli/            # Terminal input
      core/           # Types (AgentState, AgentMessage)
      harness/        # Agent loop (stub for now)
    parts/            # Step-by-step lessons
  docs/               # Documentation site (this page)`}</Pre>

      <H3>Commands</H3>
      <Table
        headers={["Command", "What it does"]}
        rows={[
          ["bun install", "Install dependencies"],
          ['bun run dev -- "<task>"', "Run with hot reload"],
          ['bun start -- "<task>"', "Run once"],
          ["bun test", "Run tests"],
          ["bun run build", "Build the docs site for production"],
        ]}
      />

      <H3>Learn the concepts</H3>
      <P>
        Each concept is documented in this sidebar. For a step-by-step lesson plan that walks
        through the code changes, see <Code>lemda-agent/parts/README.MD</Code>.
      </P>
    </Page>
  );
}

export function App() {
  const [section, setSection] = useState<Section>("overview");
  const [topTab, setTopTab] = useState<TopTab>("documentation");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [stars, setStars] = useState<number | null>(null);

  useEffect(() => {
    fetch("https://api.github.com/repos/aiLemda/lemda-cloud")
      .then((r) => r.json())
      .then((d) => setStars(d.stargazers_count))
      .catch(() => {});
  }, []);

  const onSearchOpen = useCallback(() => { setSearchOpen(true); setSearchQuery(""); }, []);
  const onSearchClose = useCallback(() => setSearchOpen(false), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
        if (!searchOpen) setSearchQuery("");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [searchOpen]);

  const handleSearchSelect = useCallback((s: Section) => {
    setSection(s);
    setTopTab("documentation");
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SearchModal
        open={searchOpen}
        query={searchQuery}
        setQuery={setSearchQuery}
        onSelect={handleSearchSelect}
        onClose={onSearchClose}
      />
      <nav className="h-12 border-b border-border bg-sidebar flex items-center px-4 gap-4 sticky top-0 z-40">
        <a href="/" className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity">
          <img src={lemdaLogo} alt="Lemda" className="size-6 rounded" />
          <span className="text-sm font-bold tracking-tight">Lemda</span>
        </a>
        <div className="flex gap-0.5">
          {TOP_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setTopTab(tab.id)}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs transition-colors",
                topTab === tab.id
                  ? "bg-sidebar-accent text-sidebar-accent-foreground font-medium"
                  : "text-sidebar-foreground/60 hover:text-sidebar-foreground",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <a
            href="https://github.com/aiLemda/lemda-cloud"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-xs text-muted-foreground/50 hover:text-muted-foreground hover:border-foreground/20 transition-colors"
          >
            <svg className="size-3.5 text-amber-500" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.751.751 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25z"/>
            </svg>
            <span className="font-medium">{stars ?? 0}</span>
          </a>
          <a
            href="https://github.com/aiLemda/lemda-cloud"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
          >
            <svg className="size-4" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
            </svg>
          </a>
          <button onClick={onSearchOpen} className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">
            <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <kbd className="hidden md:inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-medium border border-border">
              <span className="text-[9px]">&#8984;</span>K
            </kbd>
          </button>
        </div>
      </nav>
      <div className="flex">
        {topTab === "documentation" ? (
          <>
            <Sidebar active={section} onSelect={setSection} onSearchOpen={onSearchOpen} />
            <Content section={section} />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[calc(100vh-3rem)]">
            <div className="text-center">
              <p className="text-muted-foreground text-sm">
                {topTab === "home" && "Welcome to Lemda docs."}
                {topTab === "news" && "No news yet."}
                {topTab === "packages" && "No packages published yet."}
                {topTab === "models" && "No custom models configured yet."}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
